import { interpolate } from '../../../../global';
import { ILanguageResources, ITranslationModule } from '../../../../interfaces';

// ID class for franchisee-project-detail-view
class Id {
  private constructor() {}

  public static readonly prefix = 'UI.scripts.dealer-franchisee.projects.views.FranchiseeProjectDetailView.';
  public static readonly selectClient = `${Id.prefix}selectClient`;
  public static readonly selectContact = `${Id.prefix}selectContact`;
  public static readonly selectPaymentProfile = `${Id.prefix}selectPaymentProfile`;
  public static readonly provideStreetAddress = `${Id.prefix}provideStreetAddress`;
  public static readonly addClient = `${Id.prefix}addClient`;
  public static readonly setDefaultShippingAddressQuestion = `${Id.prefix}setDefaultShippingAddressQuestion`;
  public static readonly setDefaultShippingAddressTitle = `${Id.prefix}setDefaultShippingAddressTitle`;
  public static readonly newButton = `${Id.prefix}newButton`;
  public static readonly client = `${Id.prefix}client`;
  public static readonly contact = `${Id.prefix}contact`;
  public static readonly paymentProfile = `${Id.prefix}paymentProfile`;
  public static readonly termsConditions = `${Id.prefix}termsConditions`;
  public static readonly projectSiteInstallationAddress = `${Id.prefix}projectSiteInstallationAddress`;
  public static readonly title = `${Id.prefix}title`;
  public static readonly author = `${Id.prefix}author`;
  public static readonly projectBudget = `${Id.prefix}projectBudget`;
  public static readonly description = `${Id.prefix}description`;
  public static readonly changeQuoteAuthorsQuestion = `${Id.prefix}changeQuoteAuthorsQuestion`;
  public static readonly newProjectAuthorTitle = `${Id.prefix}newProjectAuthorTitle`;
  public static readonly lockedQuoteText = `${Id.prefix}lockedQuoteText`;
  public static readonly lockedQuotesWarning = `${Id.prefix}lockedQuotesWarning`;
  public static readonly quotesAuthorsChanged = `${Id.prefix}quotesAuthorsChanged`;
  public static readonly unableToChangeQuoteAuthors = `${Id.prefix}unableToChangeQuoteAuthors`;
  public static readonly noQuotesRequireUpdating = `${Id.prefix}noQuotesRequireUpdating`;
  public static readonly selectClientFirst = `${Id.prefix}selectClientFirst`;
}

// Lang wrapper for franchisee-project-detail-view
export class LangFranchiseeProjectDetailView {
  private constructor() {}

  public static selectClient(): string {
    return interpolate(Id.selectClient);
  }

  public static selectContact(): string {
    return interpolate(Id.selectContact);
  }

  public static selectPaymentProfile(): string {
    return interpolate(Id.selectPaymentProfile);
  }

  public static provideStreetAddress(): string {
    return interpolate(Id.provideStreetAddress);
  }

  public static addClient(): string {
    return interpolate(Id.addClient);
  }

  public static setDefaultShippingAddressQuestion(): string {
    return interpolate(Id.setDefaultShippingAddressQuestion);
  }

  public static setDefaultShippingAddressTitle(): string {
    return interpolate(Id.setDefaultShippingAddressTitle);
  }

  public static newButton(): string {
    return interpolate(Id.newButton);
  }

  public static client(): string {
    return interpolate(Id.client);
  }

  public static contact(): string {
    return interpolate(Id.contact);
  }

  public static paymentProfile(): string {
    return interpolate(Id.paymentProfile);
  }

  public static termsConditions(): string {
    return interpolate(Id.termsConditions);
  }

  public static projectSiteInstallationAddress(): string {
    return interpolate(Id.projectSiteInstallationAddress);
  }

  public static title(): string {
    return interpolate(Id.title);
  }

  public static author(): string {
    return interpolate(Id.author);
  }

  public static projectBudget(): string {
    return interpolate(Id.projectBudget);
  }

  public static description(): string {
    return interpolate(Id.description);
  }

  public static changeQuoteAuthorsQuestion(): string {
    return interpolate(Id.changeQuoteAuthorsQuestion);
  }

  public static newProjectAuthorTitle(): string {
    return interpolate(Id.newProjectAuthorTitle);
  }

  public static lockedQuoteText(params: { quoteTitle: string; username: string }): string {
    return interpolate(Id.lockedQuoteText, params);
  }

  public static lockedQuotesWarning(params: { lockedQuoteText: string }): string {
    return interpolate(Id.lockedQuotesWarning, params);
  }

  public static quotesAuthorsChanged(): string {
    return interpolate(Id.quotesAuthorsChanged);
  }

  public static unableToChangeQuoteAuthors(): string {
    return interpolate(Id.unableToChangeQuoteAuthors);
  }

  public static noQuotesRequireUpdating(): string {
    return interpolate(Id.noQuotesRequireUpdating);
  }

  public static selectClientFirst(): string {
    return interpolate(Id.selectClientFirst);
  }
}

// Module registration for franchisee-project-detail-view
export class FranchiseeProjectDetailViewModule implements ITranslationModule {
  registerItems(register: ILanguageResources) {
    // Validation
    register.add(Id.selectClient, "Please select a {{glossary 'client'}}", 'Validation: client required');
    register.add(Id.selectContact, "Please select a {{glossary 'contact'}}", 'Validation: contact required');
    register.add(
      Id.selectPaymentProfile,
      "Please select a {{glossary 'payment-profile'}}",
      'Validation: payment profile required'
    );
    register.add(
      Id.provideStreetAddress,
      'Please provide a Street Address',
      `Validation error for when a filled address is missing the street address`
    );
    // Dialogs
    register.add(Id.addClient, "Add {{glossary 'client'}}", 'Dialog title: add client');
    register.add(
      Id.setDefaultShippingAddressQuestion,
      'Would you like to set the project address as the default shipping address on any subsequent quotes added to this project?',
      'Confirmation question for setting default shipping address'
    );
    register.add(
      Id.setDefaultShippingAddressTitle,
      'Set Default Shipping Address',
      'Dialog title for setting default shipping address'
    );
    // Buttons/Labels
    register.add(Id.newButton, 'New', 'Button: New');
    register.add(Id.client, "{{glossary 'client'}}", 'Label: client');
    register.add(Id.contact, "{{glossary 'contact'}}", 'Label: contact');
    register.add(Id.paymentProfile, "{{glossary 'payment-profile'}}", 'Label: payment profile');
    register.add(Id.termsConditions, "{{glossary 'terms-conditions'}}", 'Label: terms and conditions');
    register.add(
      Id.projectSiteInstallationAddress,
      "{{glossary 'project'}} (Site Installation) Address",
      'Label: project site installation address'
    );
    register.add(Id.title, 'Title', 'Label: title');
    register.add(Id.author, 'Author', 'Label: author');
    register.add(Id.projectBudget, "{{glossary 'project'}} Budget", 'Label: project budget');
    register.add(Id.description, 'Description', 'Label: description');
    // Author change dialog
    register.add(
      Id.changeQuoteAuthorsQuestion,
      "Would you like the new Author of the {{glossary 'project'}} to inherit the ownership of all the Active {{glossary 'quote:plural'}} in this Project?",
      'Confirmation question for changing quote authors'
    );
    register.add(Id.newProjectAuthorTitle, "New {{glossary 'project'}} Author", 'Dialog title for new project author');
    register.add(
      Id.lockedQuoteText,
      '<b>{{quoteTitle}}</b> is currently being edited by <b>{{username}}</b>.',
      'Locked quote text'
    );
    register.add(
      Id.lockedQuotesWarning,
      'The system cannot change authorship of locked quotes.<br/>{{lockedQuoteText}}',
      'Warning for locked quotes'
    );
    register.add(
      Id.quotesAuthorsChanged,
      "{{glossary 'quote:plural'}} authors have been changed",
      'Success message for quote authors changed'
    );
    register.add(
      Id.unableToChangeQuoteAuthors,
      "Unable to change {{glossary 'quote:plural'}} authors",
      'Warning for unable to change quote authors'
    );
    register.add(
      Id.noQuotesRequireUpdating,
      "No {{glossary 'quote:plural'}} require updating",
      'Info: no quotes require updating'
    );
    // Picker
    register.add(Id.selectClientFirst, "Please select a {{glossary 'client'}} first.", 'Warning: select client first');
  }
}
